import { useEffect } from "react";
import { ShowBase } from "ra-core";
import { useNavigate, useSearchParams } from "react-router";
import { getClientShowPath, getContactShowPath } from "@/app/routing";
import { useIsMobile } from "@/hooks/use-mobile";
import { ClientShowContent } from "@/modules/clients/ClientShowContent";
import { ContactShowContent } from "@/modules/contacts/ContactShowContent";

const PREVIEW_PARAMS = ["company", "contact", "lead", "stage"];

/** Selected record of the Accounts List (`?company=` or `?contact=`). */
export const useAccountsPanelSelection = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const companyId = searchParams.get("company");
  const contactId = searchParams.get("contact") ?? searchParams.get("lead");
  const close = () => {
    const next = new URLSearchParams(searchParams);
    for (const key of PREVIEW_PARAMS) next.delete(key);
    setSearchParams(next, { replace: true });
  };
  return { companyId, contactId, close, open: Boolean(companyId || contactId) };
};

/**
 * Record panel docked next to the Accounts List: same account/contact view
 * as the full page, in panel mode. Phones open the full page instead.
 */
/** Each record fades in as it opens (the panel remounts per record). */
const PANEL_ENTER =
  "animate-in fade-in-0 flex h-full min-h-0 flex-col duration-200 motion-reduce:animate-none";

export const AccountsRecordPanel = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { companyId, contactId, close } = useAccountsPanelSelection();

  useEffect(() => {
    if (!isMobile) return;
    if (companyId) navigate(getClientShowPath(companyId), { replace: true });
    else if (contactId)
      navigate(getContactShowPath(contactId), { replace: true });
  }, [companyId, contactId, isMobile, navigate]);

  if (isMobile) return null;

  if (companyId) {
    return (
      <ShowBase resource="companies" id={companyId} key={`c:${companyId}`}>
        <div className={PANEL_ENTER}>
          <ClientShowContent embedded onClose={close} />
        </div>
      </ShowBase>
    );
  }
  if (contactId) {
    return (
      <ShowBase resource="contacts" id={contactId} key={`p:${contactId}`}>
        <div className={PANEL_ENTER}>
          <ContactShowContent embedded onClose={close} />
        </div>
      </ShowBase>
    );
  }
  return null;
};

/** Open the account panel on the current Accounts hub URL. */
export const buildAccountsCompanyPreviewParams = (
  current: URLSearchParams,
  companyId: string | number,
): URLSearchParams => {
  const next = new URLSearchParams(current);
  next.delete("contact");
  next.delete("lead");
  next.delete("stage");
  next.set("company", String(companyId));
  return next;
};
